import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Switch, FormControlLabel, CircularProgress, IconButton, Collapse, Paper,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';

/**
 * Reusable notification settings widget.
 * Shows a toggle for each notification type. Reads/writes via WebSocket.
 *
 * Props:
 * - sendMessage: WebSocket sendMessage function
 * - cookies: { station, staff, secret }
 * - onMessage: register a handler callback (called from parent's WS onMessage)
 * - types: array of { key: string, label: string } – notification types to show
 * - compact: boolean – if true, renders as a small icon button with popover
 */

const NOTIFICATION_TYPES = [
    { key: 'claims', label: 'Claim-Benachrichtigungen' },
];

const NotificationSettings = ({ sendMessage, cookies, registerMessageHandler, unregisterMessageHandler, types }) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);

    const notifTypes = types || NOTIFICATION_TYPES;

    const handleMessage = useCallback((msg) => {
        if (msg.task === 'respGetStationNotificationSettings') {
            setLoading(false);
            if (msg.status === 'succeeded') {
                setSettings(msg.notifications || {});
            }
        }
        if (msg.task === 'respUpdateStationNotificationSettings') {
            setSaving(false);
            if (msg.status === 'succeeded') {
                setSettings(msg.notifications || {});
            }
        }
    }, []);

    useEffect(() => {
        if (registerMessageHandler) registerMessageHandler(handleMessage);
        return () => { if (unregisterMessageHandler) unregisterMessageHandler(); };
    }, [handleMessage, registerMessageHandler, unregisterMessageHandler]);

    useEffect(() => {
        sendMessage(JSON.stringify({
            task: 'getStationNotificationSettings',
            station: cookies.station,
            staff: cookies.staff,
        }));
    }, [sendMessage, cookies.station, cookies.staff]);

    const toggleSetting = (key, value) => {
        const updated = { ...settings, [key]: value };
        setSaving(true);
        setSettings(updated);
        sendMessage(JSON.stringify({
            task: 'updateStationNotificationSettings',
            station: cookies.station,
            staff: cookies.staff,
            notifications: updated,
        }));
    };

    const hasAnyEnabled = settings && notifTypes.some(t => settings[t.key]);

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <IconButton
                size="small"
                onClick={() => setOpen(!open)}
                sx={{ color: hasAnyEnabled ? '#1976d2' : '#999' }}
            >
                {hasAnyEnabled ? <NotificationsIcon fontSize="small" /> : <NotificationsOffIcon fontSize="small" />}
            </IconButton>

            <Collapse in={open} orientation="horizontal">
                <Paper sx={{ ml: 1, px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2, border: '1px solid #e5e5e3', borderRadius: 2 }}>
                    {loading ? (
                        <CircularProgress size={16} />
                    ) : (
                        notifTypes.map(t => (
                            <FormControlLabel
                                key={t.key}
                                control={
                                    <Switch
                                        size="small"
                                        checked={!!settings?.[t.key]}
                                        onChange={(e) => toggleSetting(t.key, e.target.checked)}
                                        disabled={saving}
                                    />
                                }
                                label={<Typography sx={{ fontSize: 12 }}>{t.label}</Typography>}
                                sx={{ m: 0 }}
                            />
                        ))
                    )}
                </Paper>
            </Collapse>
        </Box>
    );
};

export default NotificationSettings;
